import { css } from 'styled-components';
import {
  TTypographyLevels,
  TTypographyTheme,
  TypographyLevel,
  typographyLevelsList,
} from './typography.types';

export const mapCaseToCss = (textCase: TypographyLevel['case']) => {
  switch (textCase) {
    case 'all-caps':
      return `
        text-transform: uppercase;
      `;
    case 'sentence':
      return `
        &:first-letter {
          text-transform: uppercase;
        }
      `;
    default:
      return '';
  }
};

const pxToRem = (px: number | string, baseFontPercentage: number, baseFontPixel: number) => {
  const base = typeof px === 'string' ? parseFloat(px.replace(/px/g, '')) : px;
  return base / baseFontPixel / (baseFontPercentage / 100) + 'rem';
};

const typographyToCss = (t: TypographyLevel) => {
  return css`
    font-family: ${t.fontFamily};
    font-weight: ${t.fontWeight};
    line-height: ${t.lineHeight};
    letter-spacing: ${t.letterSpacing};
    font-size: ${t.fontSize};
    text-decoration: ${t.textDecoration};

    ${mapCaseToCss(t.case)};
  `;
};

export const mapTypographyToCss = (theme: TTypographyTheme) => {
  return typographyLevelsList.reduce((acc, level) => {
    const cuts = Object.keys(theme[level]).reduce((ac, cut) => {
      const types = theme[level];
      type TKeys = keyof typeof types;

      ac[cut] = typographyToCss(theme[level][cut as TKeys] as TypographyLevel);

      return ac;
    }, {} as Record<string, ReturnType<typeof css>>);

    acc[level] = cuts;

    return acc;
  }, {} as Record<TTypographyLevels, Record<string, any>>);
};

export const defaultTypography: TTypographyTheme = {
  htmlFontSize: 16,
  fontFamily: "'Mulish', sans-serif",
  fontWeight: 400,
  pxToRem,
  typographyToCss,
  h1: {
    default: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 700,
      case: 'sentence',
      lineHeight: '4.25rem',
      letterSpacing: '-0.09rem',
      fontSize: '3.75rem',
      textDecoration: 'initial',
    },
  },
  h2: {
    default: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 700,
      case: 'sentence',
      lineHeight: '3rem',
      letterSpacing: '-0.06rem',
      fontSize: '2.5rem',
      textDecoration: 'initial',
    },
  },
  h3: {
    default: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 700,
      case: 'sentence',
      lineHeight: '2.25rem',
      letterSpacing: '0rem',
      fontSize: '2rem',
      textDecoration: 'initial',
    },
  },
  h4: {
    default: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 400,
      case: 'sentence',
      lineHeight: '2rem',
      letterSpacing: '0rem',
      fontSize: '1.5rem',
      textDecoration: 'initial',
    },
    allCaps: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'all-caps',
      lineHeight: '2rem',
      letterSpacing: '0rem',
      fontSize: '1.5rem',
      textDecoration: 'initial',
    },
    strong: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'sentence',
      lineHeight: '1.75rem',
      letterSpacing: '0rem',
      fontSize: '1.5rem',
      textDecoration: 'initial',
    },
  },
  h5: {
    default: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'sentence',
      lineHeight: '1.75rem',
      letterSpacing: '0rem',
      fontSize: '1.25rem',
      textDecoration: 'initial',
    },
    strong: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 700,
      case: 'sentence',
      lineHeight: '1.25rem',
      letterSpacing: '0rem',
      fontSize: '1.25rem',
      textDecoration: 'initial',
    },
  },
  h6: {
    default: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'sentence',
      lineHeight: '1.5em',
      letterSpacing: '0rem',
      fontSize: '1rem',
      textDecoration: 'initial',
    },
    strong: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 700,
      case: 'sentence',
      lineHeight: '1.5em',
      letterSpacing: '0rem',
      fontSize: '1rem',
      textDecoration: 'initial',
    },
  },
  body1: {
    default: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 400,
      case: 'none',
      lineHeight: '1.5rem',
      letterSpacing: '0rem',
      fontSize: '1rem',
      textDecoration: 'initial',
    },
    strong: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'none',
      lineHeight: '1.5rem',
      letterSpacing: '0rem',
      fontSize: '1rem',
      textDecoration: 'initial',
    },
    link: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'none',
      lineHeight: '1.5rem',
      letterSpacing: '0rem',
      fontSize: '1rem',
      textDecoration: 'underline',
    },
  },
  body2: {
    default: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 400,
      case: 'none',
      lineHeight: '1.25rem',
      letterSpacing: '0rem',
      fontSize: '0.875rem',
      textDecoration: 'initial',
    },
    strong: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'none',
      lineHeight: '1.25rem',
      letterSpacing: '0rem',
      fontSize: '0.875rem',
      textDecoration: 'initial',
    },
    link: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'none',
      lineHeight: '1.25rem',
      letterSpacing: '0rem',
      fontSize: '0.875rem',
      textDecoration: 'underline',
    },
  },
  body3: {
    default: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 400,
      case: 'none',
      lineHeight: '1rem',
      letterSpacing: '0rem',
      fontSize: '0.75rem',
      textDecoration: 'initial',
    },
    strong: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'none',
      lineHeight: '1rem',
      letterSpacing: '0rem',
      fontSize: '0.75rem',
      textDecoration: 'initial',
    },
    link: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'none',
      lineHeight: '1rem',
      letterSpacing: '0rem',
      fontSize: '0.75rem',
      textDecoration: 'underline',
    },
  },
  buttonPrimary: {
    default: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'all-caps',
      lineHeight: '1rem',
      letterSpacing: '0.01rem',
      fontSize: '0.9375rem',
      textDecoration: 'initial',
    },
  },
  buttonText: {
    default: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'sentence',
      lineHeight: '1rem',
      letterSpacing: '0.02rem',
      fontSize: '0.875rem',
      textDecoration: 'initial',
    },
  },
  overline: {
    default: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'none',
      lineHeight: '1rem',
      letterSpacing: '0rem',
      fontSize: '0.875rem',
      textDecoration: 'initial',
    },
    strong: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 700,
      case: 'none',
      lineHeight: '1rem',
      letterSpacing: '0rem',
      fontSize: '0.875rem',
      textDecoration: 'initial',
    },
  },
  captionLarge: {
    default: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'none',
      lineHeight: '1rem',
      letterSpacing: '0.02rem',
      fontSize: '0.75rem',
      textDecoration: 'initial',
    },
  },
  captionSmall: {
    default: {
      fontFamily: "'Mulish', sans-serif",
      fontWeight: 600,
      case: 'none',
      lineHeight: '0.75rem',
      letterSpacing: '0.01rem',
      fontSize: '0.625rem',
      textDecoration: 'initial',
    },
  },
};
